import { createElement, ReactElement, ReactNode } from 'react'
import ReactReconciler, { HostConfig } from 'react-reconciler'
import { match, P } from '@st/pattern'
import {
  EdgeInsets,
  paintPage,
  PaintOp,
  Rect,
  RenderBox,
  RenderedAnnotation,
  RenderedPage,
  renderPage,
  ViewportRenderBox
} from './rendering'

type DocumentProps = {
  title?: string
  children: ReactNode
}

type PageProps = {
  width: number
  height: number
  children: ReactNode
}

type DocumentElementType = 'RenderBox' | 'Document' | 'Page'
type Props = { [key: string]: any }
type TextNode = string
type HostContext = unknown
type UpdatePayload = unknown
type ChildSet = unknown
type TimeoutHandle = unknown
type NoTimeout = number
type SuspenseInstance = unknown
type HydratableInstance = unknown
type PublicInstance = unknown

type DocumentNode = {
  type: 'Document'
  props: DocumentProps
  children: Array<DocumentNode | PageNode>
}

type PageNode = {
  type: 'Page'
  props: PageProps
  children: RenderBox[]
}

type Instance = RenderBox | DocumentNode | PageNode
type Container = { children: Instance[] } | ViewportRenderBox

export type GraphicsDocument = {
  title?: string
  pages: GraphicsPage[]
  outline: GraphicsOutline
}

export type GraphicsPage = {
  size: Rect
  content: PaintOp
  annotations: RenderedAnnotation[]
}

export type GraphicsOutline = {
  children: GraphicsOutlineEntry[]
}

export type GraphicsOutlineEntry = {
  title: string
  destination: { page: number }
  children: GraphicsOutlineEntry[]
}

export function Document({ title, children }: DocumentProps) {
  return createElement('Document', { title }, children)
}

export function Page({ width, height, children }: PageProps) {
  return createElement('Page', { width, height }, children)
}

export function renderElement(renderBox: RenderBox, ...children: ReactNode[]) {
  return createElement('RenderBox', { renderBox }, ...children)
}

export function renderToRenderedPage(
  children: ReactNode,
  size: { width: number; height: number }
): RenderedPage {
  return renderPage(renderChildrenToRenderBoxRoot(children, size))
}

function renderChildrenToRenderBoxRoot(
  children: ReactNode,
  size: { width: number; height: number }
): ViewportRenderBox {
  const container = new ViewportRenderBox(size)
  const root = documentReconciler.createContainer(
    container,
    0,
    null,
    true,
    false,
    '',
    () => null,
    null
  )

  documentReconciler.updateContainer(children, root, null)

  return container
}

export type LaidOutPage = RenderedPage & { size: Rect }

/**
 * Pack whole layout roots in source order. Rows measure themselves using normal
 * constraints; painting retains that geometry rather than laying them out a
 * second time against a tight page. A root must fit a fresh printable page.
 */
export function layoutPages({
  children,
  pageSize,
  padding
}: {
  children: ReactNode
  pageSize: { width: number; height: number }
  padding: EdgeInsets
}): LaidOutPage[] {
  const width = pageSize.width - padding.left - padding.right
  const height = pageSize.height - padding.top - padding.bottom
  if (width <= 0 || height <= 0) throw new Error('Page padding leaves no printable area')
  const source = renderChildrenToRenderBoxRoot(children, pageSize)
  const pages: LaidOutPage[] = []
  let page = new ViewportRenderBox(pageSize)
  page.box = { x: 0, y: 0, ...pageSize }
  let used = 0
  const finishPage = () => {
    pages.push({ size: { ...page.box }, ...paintPage(page) })
    page = new ViewportRenderBox(pageSize)
    page.box = { x: 0, y: 0, ...pageSize }
    used = 0
  }
  for (const child of source.children) {
    child.performLayout({ minWidth: width, maxWidth: width, minHeight: 0, maxHeight: Infinity })
    if (!Number.isFinite(child.box.height) || child.box.height > height) {
      throw new Error(`${child.constructor.name} does not fit a printable page (${height})`)
    }
    if (used + child.box.height > height && page.children.length > 0) finishPage()
    child.box.x = padding.left
    child.box.y = padding.top + used
    page.add(child)
    used += child.box.height
  }
  if (page.children.length > 0) finishPage()
  return pages
}

/** The same serialization boundary serves fixed pages and packed pages. */
export function renderedPagesToGraphicsDocument(
  pages: LaidOutPage[],
  title?: string
): GraphicsDocument {
  return {
    title,
    outline: {
      children: pages.flatMap((page, index) =>
        page.bookmarks.map((bookmark) => ({
          title: bookmark.title,
          destination: { page: index + 1 },
          children: []
        }))
      )
    },
    pages: pages.map((page) => ({
      size: page.size,
      content: page.content,
      annotations: page.annotations.map(stripRuntimeCallback)
    }))
  }
}

export function renderDocumentToGraphicsDocument(
  element: ReactElement<DocumentProps>
): GraphicsDocument {
  const container: Container = { children: [] }
  const root = documentReconciler.createContainer(
    container,
    0,
    null,
    true,
    false,
    '',
    () => null,
    null
  )

  documentReconciler.updateContainer(element, root, null)

  if (container.children.length != 1 || !isDocumentNode(container.children[0])) {
    throw new Error('renderDocumentToGraphicsDocument expects a single <Document /> root')
  }

  const documentNode = container.children[0]
  const pages = documentNode.children.map((page) => {
    if (!isPageNode(page)) throw new Error('Document children must be <Page /> elements')
    const viewport = new ViewportRenderBox({ width: page.props.width, height: page.props.height })
    for (const child of page.children) viewport.add(child)
    return {
      ...renderPage(viewport),
      size: { x: 0, y: 0, width: page.props.width, height: page.props.height }
    }
  })
  return renderedPagesToGraphicsDocument(pages, documentNode.props.title)
}

function createDocumentInstance(type: DocumentElementType, props: Props): Instance {
  if (type == 'Document') {
    return { type, props: props as DocumentProps, children: [] }
  }
  if (type == 'Page') {
    return { type, props: props as PageProps, children: [] }
  }
  return props.renderBox
}

const hostConfig: HostConfig<
  DocumentElementType,
  Props,
  Container,
  Instance,
  TextNode,
  SuspenseInstance,
  HydratableInstance,
  PublicInstance,
  HostContext,
  UpdatePayload,
  ChildSet,
  TimeoutHandle,
  NoTimeout
> = {
  isPrimaryRenderer: false,
  supportsMutation: true,
  supportsPersistence: false,
  supportsHydration: false,
  scheduleTimeout: setTimeout,
  cancelTimeout: clearTimeout,
  noTimeout: -1,
  createInstance: createDocumentInstance,
  createTextInstance: (text) => text,
  appendChildToContainer: addChild,
  appendInitialChild: addChild,
  appendChild: addChild,
  removeChildFromContainer: removeChild,
  removeChild,
  prepareUpdate: () => null,
  getChildHostContext: () => {},
  finalizeInitialChildren: () => false,
  shouldSetTextContent: () => false,
  getRootHostContext: () => {},
  getPublicInstance: (instance) => instance,
  prepareForCommit: () => null,
  resetAfterCommit: () => {},
  preparePortalMount: () => {},
  getCurrentEventPriority: () => 0,
  getInstanceFromNode: () => null,
  beforeActiveInstanceBlur: () => {},
  afterActiveInstanceBlur: () => {},
  prepareScopeUpdate: () => {},
  getInstanceFromScope: () => null,
  detachDeletedInstance: () => null,
  clearContainer: (container) => {
    container.children = []
  }
}

const documentReconciler = ReactReconciler(hostConfig)

function addChild(parent: Instance | Container, child: Instance | TextNode) {
  if (typeof child === 'string') {
    if (child.length === 0) return
    throw new Error(`Unexpected text node in document-rendering document: ${JSON.stringify(child)}`)
  }

  if (isRenderBox(parent)) {
    if (!isRenderBox(child)) throw new Error('RenderBox children must be RenderBox instances')
    parent.add(child)
    return
  }

  if (isPageNode(parent)) {
    if (!isRenderBox(child)) throw new Error('Page children must be RenderBox instances')
    parent.children.push(child)
    return
  }

  if (isDocumentNode(parent)) {
    if (!isPageNode(child)) throw new Error('Document children must be Page instances')
    parent.children.push(child)
    return
  }

  parent.children.push(child)
}

function removeChild(parent: Instance | Container, child: Instance | TextNode) {
  if (typeof child === 'string') {
    throw new Error('Unexpected text node removal in document-rendering document')
  }

  if (isRenderBox(parent)) {
    if (!isRenderBox(child)) throw new Error('RenderBox children must be RenderBox instances')
    parent.removeChild(child)
    return
  }

  const index = parent.children.indexOf(child as never)
  if (index != -1) parent.children.splice(index, 1)
}

function stripRuntimeCallback(annotation: RenderedAnnotation): RenderedAnnotation {
  return match(annotation)
    .with(
      P.union({ type: 'text_field' }, { type: 'checkbox' }, { type: 'radio' }, { type: 'select' }),
      ({ onChange: _onChange, ...serializable }) => serializable
    )
    .otherwise((serializable) => serializable)
}

function isRenderBox(value: unknown): value is RenderBox {
  return value instanceof RenderBox
}

function isDocumentNode(value: unknown): value is DocumentNode {
  return !!value && typeof value == 'object' && (value as DocumentNode).type == 'Document'
}

function isPageNode(value: unknown): value is PageNode {
  return !!value && typeof value == 'object' && (value as PageNode).type == 'Page'
}
